import * as React from 'react';
import PhoneInput, { Country, isValidPhoneNumber, getCountryCallingCode } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { cn } from '@/lib/utils';
import { useGeoDetection } from '@/hooks/useGeoDetection';

interface InternationalPhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  placeholder?: string;
  defaultCountry?: Country;
  autoDetectCountry?: boolean;
  className?: string;
  label?: string;
  required?: boolean;
}

const InternationalPhoneInput = React.forwardRef<HTMLInputElement, InternationalPhoneInputProps>(
  ({ 
    value, 
    onChange, 
    error, 
    placeholder = 'Enter your mobile number', 
    defaultCountry = 'IN',
    autoDetectCountry = true,
    className,
    label = 'Mobile / WhatsApp Number',
    required = true,
  }, ref) => {
    const { country: detectedCountry, isLoading } = useGeoDetection(defaultCountry);
    
    // Use detected country if auto-detection is enabled, otherwise use default
    const effectiveCountry = autoDetectCountry ? detectedCountry : defaultCountry;

    return (
      <div className={cn('phone-input-wrapper', className)}>
        {label && (
          <label className="block text-sm font-medium text-foreground mb-1.5">
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </label>
        )}
        <PhoneInput
          international
          countryCallingCodeEditable={false}
          defaultCountry={effectiveCountry}
          value={value}
          onChange={(val) => onChange(val || '')}
          placeholder={placeholder}
          className={cn(
            'flex h-11 w-full rounded-md border bg-background text-base ring-offset-background transition-colors',
            'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
            error ? 'border-destructive' : 'border-input',
            'phone-input-international',
            isLoading && 'opacity-90'
          )}
        />
      </div>
    );
  }
);

InternationalPhoneInput.displayName = 'InternationalPhoneInput';

export { InternationalPhoneInput, isValidPhoneNumber, getCountryCallingCode };
